// src/components/store/sections/product-cards/PastelGlassyProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingBag, Clock, Flame, Heart, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const SHAPES_THEMES = [
  {
    stageShape: "rounded-t-[75px] rounded-b-[20px]",
    bgGradient: "from-[#a7f3d0] via-[#ecfdf5] to-[#6ee7b7]",
    pedestalGlow: "rgba(16, 185, 129, 0.45)",
    categoryBadge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    priceColor: "text-emerald-700",
    btnGradient: "from-emerald-600 via-teal-600 to-green-600",
  },
  {
    stageShape: "rounded-tl-[75px] rounded-br-[75px] rounded-tr-[22px] rounded-bl-[22px]",
    bgGradient: "from-[#4a044e] via-[#2e0236] to-[#16001d]",
    pedestalGlow: "rgba(217, 70, 239, 0.55)",
    categoryBadge: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
    priceColor: "text-fuchsia-600",
    btnGradient: "from-purple-600 via-fuchsia-600 to-pink-600",
  },
  {
    stageShape: "rounded-t-[26px] rounded-b-[70px]",
    bgGradient: "from-[#fef08a] via-[#fffbeb] to-[#fde047]",
    pedestalGlow: "rgba(245, 158, 11, 0.5)",
    categoryBadge: "bg-amber-100 text-amber-900 border-amber-300",
    priceColor: "text-amber-800",
    btnGradient: "from-amber-600 via-orange-600 to-red-600",
  },
  {
    stageShape: "rounded-tr-[75px] rounded-bl-[75px] rounded-tl-[22px] rounded-br-[22px]",
    bgGradient: "from-[#083344] via-[#051d29] to-[#01090f]",
    pedestalGlow: "rgba(6, 182, 212, 0.55)",
    categoryBadge: "bg-cyan-100 text-cyan-800 border-cyan-300",
    priceColor: "text-cyan-600",
    btnGradient: "from-blue-600 via-cyan-600 to-teal-600",
  },
];

export default function PastelGlassyProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    comparePrice,
    discountPercent,
    added,
    wishlisted,
    setWishlisted,
    timer,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = SHAPES_THEMES[index % SHAPES_THEMES.length];

  return (
    <div className="w-full h-full rounded-[30px] p-4 bg-white border border-slate-200/80 shadow-md hover:shadow-xl flex flex-col justify-between relative overflow-hidden group transition-all duration-300 select-none">
      <div>
        <div className="flex items-center justify-between gap-2 mb-2.5 z-10 relative">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-rose-500 text-white font-black text-[10px] shadow-xs">
            <Flame className="w-3 h-3 fill-white" />
            <span>{discountPercent}% OFF</span>
          </span>

          <button
            type="button"
            onClick={() => setWishlisted(!wishlisted)}
            className="w-7 h-7 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-rose-500 transition-colors cursor-pointer"
          >
            <Heart className={`w-3.5 h-3.5 ${wishlisted ? "fill-rose-500 text-rose-500" : ""}`} />
          </button>
        </div>

        <Link
          href={productUrl}
          className={`relative w-full h-44 ${theme.stageShape} bg-gradient-to-b ${theme.bgGradient} overflow-hidden flex items-center justify-center p-3 mb-3 shadow-inner border border-white/40 block`}
        >
          <div
            style={{ backgroundColor: theme.pedestalGlow }}
            className="absolute bottom-2 w-32 h-6 rounded-full blur-md"
          />
          <div className="relative w-32 h-32 z-10">
            <Image
              src={productImg}
              alt={productName}
              fill
              unoptimized
              className="object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-xl"
            />
          </div>
          <span
            className={`absolute bottom-2 left-2.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold border shadow-2xs truncate max-w-[140px] ${theme.categoryBadge}`}
          >
            {categoryName}
          </span>
        </Link>

        <div className="space-y-1">
          <Link
            href={productUrl}
            className="text-sm font-black text-slate-900 hover:text-purple-600 transition-colors line-clamp-1 block"
          >
            {productName}
          </Link>
          <div className="flex items-center gap-1 text-[11px] font-bold text-amber-500">
            <Star className="w-3 h-3 fill-amber-400" />
            <span>{rating.toFixed(1)}</span>
          </div>
          <div className="flex items-baseline gap-1.5 mt-1 font-mono">
            <span className={`text-lg font-black ${theme.priceColor}`}>
              ৳{currentPrice.toLocaleString()}
            </span>
            {comparePrice > currentPrice && (
              <span className="text-[10px] text-slate-400 line-through">
                ৳{comparePrice.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-2 mt-3 pt-2.5 border-t border-slate-100">
        <div className="bg-slate-50 rounded-xl p-1.5 border border-slate-100 flex items-center justify-between text-[10px] font-mono font-bold text-slate-700">
          <span className="text-rose-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Hurry:
          </span>
          <span>
            {String(timer.hours).padStart(2, "0")}h:{String(timer.minutes).padStart(2, "0")}m:
            <span className="text-rose-600">{String(timer.seconds).padStart(2, "0")}s</span>
          </span>
        </div>

        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className={`w-full py-2.5 px-4 rounded-full bg-gradient-to-r ${theme.btnGradient} text-white font-black text-[11px] uppercase tracking-wider shadow-md flex items-center justify-center gap-1.5 active:scale-95 transition-all cursor-pointer`}
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingBag className="w-3.5 h-3.5" />}
          <span>{added ? "Added to Cart" : "Add to Cart"}</span>
        </button>
      </div>
    </div>
  );
}
